'use client';

import { useTranslations } from 'next-intl';
import clsx from 'clsx';
import { CHANEL_BRAND_ID, SchemaOrgItemProp, SchemaOrgItemType } from '@headless-workspace/config';
import {
    MediaType,
    ProductDetailPricesContainer,
    ProductDetailPushLoyalty,
    ProductType,
    useClientData,
    useProductVariantDetailContext,
} from '@headless-workspace/domain/common/client';
import { ProductDetailValue, VipBrandsValue } from '@headless-workspace/domain/common/server';
import { GlowClickable, GlowTextBody, PropsWithFontFamily, PropsWithLocale } from '@headless-workspace/glow-ds';
import { Component } from '@headless-workspace/typings';
import { ObjectHelpers, StringHelpers } from '@headless-workspace/utils';
import {
    ProductDetailDeliveryZoneContainer,
    ProductInfoSection,
    ProductSeeDescription,
    useProductDetailPromotionsInformation,
} from '../../../../client';
import { ProductDetailBanners } from '../banners';
import { ProductDetailBuyNowPayLaterText } from './ProductDetailBuyNowPayLaterText';
import { ProductRating } from './ProductRating';
import { ProductTitleWithMeta } from './ProductTitleWithMeta';
import { ProductDetailAudioContainer } from './audio';
import styles from './audio/ekoo-custom.module.css';
import chanelStyles from './chanel.module.css';
import { ProductDetailSelectorContainer } from './selector';

type ProductDetailInformationProps = PropsWithLocale &
    PropsWithFontFamily & {
        product: ProductDetailValue;
        vipBrands: VipBrandsValue;
        redDotText?: string;
        onVariantHovered?: (variantId: string) => void;
    };

export const ProductDetailInformation: Component<ProductDetailInformationProps> = ({
    locale,
    product,
    vipBrands,
    fontFamily,
    redDotText,
    onVariantHovered,
}) => {
    const t = useTranslations('Discover.Pdp.information.price');

    const { getClientFlags } = useClientData();
    const isPriceLegalMentionEnabled = getClientFlags('isPriceExceptionLegalMentionsDisplayed');

    const { productVariantDetails } = useProductVariantDetailContext();
    const selectedProduct = productVariantDetails ?? product;

    const promotionInformation = useProductDetailPromotionsInformation({
        promotion: selectedProduct.promotion,
    });
    const brandTextDecoration = product.isUnderlinedBrand ? 'underline' : 'none';
    const isChanelBrand = product.brand.id === CHANEL_BRAND_ID;

    const discountPercentageLabel = selectedProduct.discountPercentage
        ? t('label.discountPercentage', {
              discountPercentage: selectedProduct.discountPercentage,
          })
        : undefined;

    const formattedTotalIndividualPrice = product.totalIndividualPrice
        ? t('label.totalIndividualPrice', {
              value: StringHelpers.formatPrice({
                  price: product.totalIndividualPrice,
                  currency: product.currency,
                  locale,
              }),
          })
        : undefined;

    const mainImage = product.medias?.find((media) => media.type === MediaType.IMAGE);
    const productImage = mainImage ? ObjectHelpers.createImageWithSrc(mainImage.src, mainImage.alt) : undefined;

    return (
        <section id={'product-detail-information-container'} className={'flex flex-col flex-1 px-1 desktop:px-0 gap-3'}>
            <section id={'product-information'} className={'flex flex-col gap-1.5'}>
                <div id={'product-sku-detail'} className={'flex flex-col gap-0.5'}>
                    <div itemProp={SchemaOrgItemProp.Brand} itemScope itemType={SchemaOrgItemType.Brand}>
                        <GlowClickable
                            id={'product-brand'}
                            href={product.brand.url}
                            content={{
                                labelElement: (
                                    <GlowTextBody
                                        className={clsx(isChanelBrand && chanelStyles.chanelBrandText)}
                                        text={product.brand.name}
                                        textDecoration={brandTextDecoration}
                                        fontFamily={fontFamily}
                                        TagName={'span'}
                                        structuredData={{
                                            itemProp: SchemaOrgItemProp.Name,
                                        }}
                                    />
                                ),
                            }}
                            structuredData={{
                                itemProp: SchemaOrgItemProp.Url,
                                content: product.brand.url,
                            }}
                        />
                    </div>
                    <ProductTitleWithMeta name={product.name} fontFamily={fontFamily} isChanelBrand={isChanelBrand} />
                    <div className={'flex flex-col gap-1'}>
                        <div className={'flex flex-col tablet:flex-row tablet:justify-between gap-1'}>
                            <ProductSeeDescription
                                productInfoTabParams={product.productInfoTabParams}
                                fontFamily={fontFamily}
                            />
                            <ProductRating
                                fontFamily={fontFamily}
                                rating={product.rating}
                                reviewCount={product.reviewCount}
                                productName={product.name?.full}
                            />
                        </div>
                        <ProductDetailPricesContainer
                            price={selectedProduct.price}
                            priorPrice={selectedProduct.priorPrice}
                            originalPrice={selectedProduct.originalPrice}
                            pricePerUnit={selectedProduct.pricePerUnit}
                            currency={product.currency}
                            locale={locale}
                            fontFamily={fontFamily}
                            promotion={selectedProduct.promotion}
                            discountPercentageLabel={discountPercentageLabel}
                            priceAlwaysBlack={selectedProduct.priceAlwaysBlack}
                            isVipBrand={product.isVipBrand}
                            promotionInformation={promotionInformation}
                            formattedTotalIndividualPrice={formattedTotalIndividualPrice}
                            isPriceLegalMentionEnabled={isPriceLegalMentionEnabled}
                        />
                    </div>

                    <ProductDetailBuyNowPayLaterText
                        buyNowPayLaterContent={product.buyNowPayLaterContent}
                        fontFamily={fontFamily}
                    />
                </div>
                {product.hasAudio && product.masterProductId && (
                    <div
                        id={'ekoo-container'}
                        className={clsx('flex items-center', 'w-full tablet:w-[343px]', styles.audioWrapper)}
                    >
                        <ProductDetailAudioContainer masterProductId={product.masterProductId} />
                    </div>
                )}
                <ProductDetailSelectorContainer
                    id={product.id}
                    productName={selectedProduct.name?.full}
                    brandName={product.brand.name}
                    locale={locale}
                    vipBrands={vipBrands}
                    isVipBrand={product.isVipBrand}
                    price={StringHelpers.formatPrice({
                        price: selectedProduct.price,
                        currency: selectedProduct.currency,
                        locale,
                    })}
                    priceBeforeDiscount={
                        selectedProduct.priceBeforeDiscount
                            ? StringHelpers.formatPrice({
                                  price: selectedProduct.priceBeforeDiscount,
                                  currency: selectedProduct.currency,
                                  locale,
                              })
                            : undefined
                    }
                    priceAlwaysBlack={selectedProduct.priceAlwaysBlack}
                    enableDiscountInfo={selectedProduct.enableDiscountInfo}
                    fontFamily={fontFamily}
                    type={product.type}
                    formats={product.formats}
                    variants={product.variants}
                    currency={product.currency}
                    squaredShade={product.squaredShade}
                    discountPercentageLabel={discountPercentageLabel}
                    isRedDot={selectedProduct.isRedDot}
                    redDotText={redDotText}
                    stock={selectedProduct.stockLevel}
                    productsSetList={product.productsSetList}
                    setHoveredVariant={onVariantHovered}
                    isPriceLegalMentionEnabled={isPriceLegalMentionEnabled}
                />
            </section>
            {product.loyalty?.pointsToEarn && (
                <ProductDetailPushLoyalty
                    currency={product.currency}
                    loyaltyInformation={product.loyalty}
                    fontFamily={fontFamily}
                    locale={locale}
                    isVipBrand={product.isVipBrand}
                />
            )}

            {product.type !== ProductType.PRODUCT_SET && <ProductDetailDeliveryZoneContainer locale={locale} />}
            <ProductInfoSection
                productInfoTabParams={product.productInfoTabParams}
                fontFamily={fontFamily}
                isEngravable={product.isEngravable}
                productImage={productImage}
            />
            <ProductDetailBanners banners={product.banners} />
        </section>
    );
};
